import { SupportedLanguage } from '@vimond/i18n';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getEnvVar } from '../../utils/env';
import {
  filterLocales,
  formatLocales,
  getEnabledLocales,
  sortLocales,
} from '../../utils/locales/localeUtils';
import {
  GetAllProductGroupsResponse,
  isValidGetAllProductGroupsResponse,
  isValidProductGroupResponse,
  isValidProductPaymentResponse,
  ProductGroup,
  ProductPaymentResponse,
  TvodProduct,
} from './types';
import { intervalToHours, isoPeriodToHours, parseIsoPeriod } from './utils';

export type Locale = {
  default: boolean;
  language: string;
  country: string;
  localeId: string;
  locale: SupportedLanguage;
};

type Locales = {
  locales: Locale[];
};

const restAPIUrl = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.rest-api.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv`;

const getAllowedTvodPaymentIds = (): number[] => {
  const raw = getEnvVar('NEXT_PUBLIC_TVOD_PRODUCT_PAYMENT_IDS');
  if (!raw) return [];

  const ids = raw
    .split(',')
    .map((value) => Number.parseInt(value.trim(), 10))
    .filter((value) => !Number.isNaN(value));

  return ids;
};

const ALLOWED_TVOD_PAYMENT_IDS = getAllowedTvodPaymentIds();

const buildRestApiRequestOptions = (jwt: string): RequestInit => {
  const bearerToken = `Bearer ${jwt}`;
  return {
    headers: {
      Authorization: bearerToken,
      'Content-Type': 'application/json;v=3',
      Accept: 'application/json;v=3',
    },
  };
};

export const getLocales = async (): Promise<Locale[]> => {
  const enabledLocales = getEnabledLocales();

  try {
    const locales = await fetchLocalesFromApi();
    const filteredLocales = filterLocales(locales, enabledLocales);
    const sortedLocales = sortLocales(filteredLocales);
    return formatLocales(sortedLocales);
  } catch (error) {
    console.error('Failed to fetch locales:', error);
    throw error;
  }
};

const fetchLocalesFromApi = async (): Promise<Locale[]> => {
  const headers = {
    accept: 'application/json;v=3',
  };
  const requestOptions: RequestInit & { next?: { revalidate?: number } } = {
    method: 'GET',
    headers,
    redirect: 'follow',
    next: {
      revalidate: 3600,
    },
  };
  const response = await fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.rest-api.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/vcc/locales`,
    requestOptions,
  );
  const json: Locales = (await response.json()) as unknown as Locales;
  return json.locales;
};

export const authenticateUserInRestAPI = async () => {
  const PAYMENTS_ENABLED = getEnvVar('NEXT_PUBLIC_PAYMENTS_ENABLED') === 'true';
  try {
    const session = await fetchAuthSession();
    if (!PAYMENTS_ENABLED) {
      const { idToken } = session?.tokens ?? {};
      if (!idToken) {
        throw new Error('No id token found in session object');
      }
      const bearerToken = `Bearer ${idToken.toString()}`;
      const restAPIUrl = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.rest-api.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv`;
      await fetch(`${restAPIUrl}/api/authentication/user`, {
        headers: { Authorization: bearerToken },
      });
    }
  } catch (error) {
    console.error('Authentication towards Vimond failed:', error);
    throw error;
  }
};

export const getAllProductGroups = async (
  jwt: string,
): Promise<GetAllProductGroupsResponse> => {
  try {
    const response = await fetch(
      `${restAPIUrl}/api/web/productgroup?expand=products,metadata,productPayments`,
      buildRestApiRequestOptions(jwt),
    );
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const json = (await response.json()) as unknown;
    if (!isValidGetAllProductGroupsResponse(json)) {
      throw new Error('Invalid response from Rest API');
    }
    return {
      productGroups: json.productGroups.filter(
        (pg) =>
          pg.productsUri.products &&
          (!Array.isArray(pg.productsUri.products) ||
            pg.productsUri.products.length > 0),
      ),
    };
  } catch (error) {
    console.error('Failed to get all product groups:', error);
    return { productGroups: [] };
  }
};

export const getProductGroup = async (
  jwt: string,
  productGroupId: number,
): Promise<GetAllProductGroupsResponse> => {
  try {
    const response = await fetch(
      `${restAPIUrl}/api/web/productgroup/${productGroupId}?expand=products,metadata,productPayments`,
      buildRestApiRequestOptions(jwt),
    );
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const json = (await response.json()) as unknown;
    // Single product group endpoint returns an object, not an array
    if (!isValidProductGroupResponse(json)) {
      throw new Error(
        'Invalid product group payload: expected id, uri, name, productsUri.products',
      );
    }
    // Wrap single product group in array format for consistent return type
    const productGroup: ProductGroup = json;
    const hasProducts =
      productGroup.productsUri.products &&
      (!Array.isArray(productGroup.productsUri.products) ||
        productGroup.productsUri.products.length > 0);

    return {
      productGroups: hasProducts ? [productGroup] : [],
    };
  } catch (error) {
    console.error(`Failed to get product group ${productGroupId}:`, error);
    return { productGroups: [] };
  }
};

export const getProductPayments = async (
  jwt: string,
  productGroupId: number,
  productId: number,
): Promise<ProductPaymentResponse> => {
  const response = await fetch(
    `${restAPIUrl}/api/web/productgroup/${productGroupId}/products/${productId}/productPayments`,
    buildRestApiRequestOptions(jwt),
  );
  const json = (await response.json()) as unknown;
  if (!isValidProductPaymentResponse(json)) {
    throw new Error('Invalid response from Rest API');
  }
  return json;
};

export const getProductPaymentsByProductId = async (
  jwt: string,
  productId: number,
) => {
  const productGroupsData = await getAllProductGroups(jwt);
  const product = (productGroupsData.productGroups ?? [])
    .flatMap((pg) => pg.productsUri?.products)
    .filter(Boolean)
    .find((p) => p?.id === productId);
  if (!product) {
    throw new Error('Product not found');
  }
  const productGroupId = product.productGroupId;
  return getProductPayments(jwt, productGroupId, productId);
};

/**
 * Find TVOD products for development/testing
 * Returns products with paymentType: "SINGLE_PROGRAM" and their Stripe metadata
 * If NEXT_PUBLIC_TVOD_PRODUCT_GROUP_ID is set, fetches only that specific product group
 */
export const getTVODProducts = async (jwt: string): Promise<TvodProduct[]> => {
  // Check if a specific product group ID is configured for TVOD
  const tvodProductGroupIdRaw = getEnvVar('NEXT_PUBLIC_TVOD_PRODUCT_GROUP_ID');
  const tvodProductGroupId = tvodProductGroupIdRaw
    ? Number.parseInt(tvodProductGroupIdRaw.trim(), 10)
    : null;

  // Fetch either specific product group or all product groups
  const productGroupsData =
    tvodProductGroupId && !Number.isNaN(tvodProductGroupId)
      ? await getProductGroup(jwt, tvodProductGroupId)
      : await getAllProductGroups(jwt);

  const allTvodProducts = productGroupsData.productGroups.flatMap((pg) => {
    // Get stripe product ID if available
    const stripeProductId =
      pg.metadata?.entries?.['stripe-product-id']?.[0]?.value;

    return (pg.productsUri?.products ?? [])
      .filter(
        (product) =>
          product?.paymentPlan?.paymentType === 'SINGLE_PROGRAM' &&
          product?.productStatus === 'ENABLED',
      )
      .map((product) => {
        const periodIso =
          product.paymentPlan?.periodIso ?? product.paymentPlan?.period;
        const { intervalCount, intervalUnit } = parseIsoPeriod(periodIso);
        const rentalDurationHours =
          isoPeriodToHours(periodIso) ??
          intervalToHours(intervalCount, intervalUnit);

        return {
          productId: product.id,
          productGroupId: pg.id,
          stripeProductId: stripeProductId ?? '',
          vimondProductPaymentIds:
            product.productPayments?.productPaymentList?.map((pp) => pp.id) ??
            [],
          price: product.price ?? 0,
          currency: product.currency ?? 'NOK',
          period: periodIso,
          name: product.paymentPlan?.name ?? 'Unknown',
          intervalCount,
          intervalUnit,
          rentalDurationHours,
        };
      })
      .filter((product) => product.vimondProductPaymentIds.length > 0);
  });

  if (ALLOWED_TVOD_PAYMENT_IDS.length === 0) {
    return allTvodProducts;
  }

  return allTvodProducts.filter((product) =>
    product.vimondProductPaymentIds.some((id) =>
      ALLOWED_TVOD_PAYMENT_IDS.includes(id),
    ),
  );
};
